import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { 
  Users, 
  Calendar, 
  Award, 
  FileText, 
  DollarSign, 
  Activity 
} from "lucide-react";
import ResourcePage from "../../components/ResourcePage";
import { api } from "../../axios/api";
import { notify } from "../../utils/toastAlert";

const ProdutividadeUtilizadores = () => {
  const { user, empresa } = useSelector((state) => state.user);
  const [loading, setLoading] = useState(false);
  const [dadosProdutividade, setDadosProdutividade] = useState(null);

  const [filtros, setFiltros] = useState({
    inicio: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    fim: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split('T')[0]
  });

  const fetchProdutividade = async () => {
    try {
      setLoading(true);
      const response = await api.post("/caixa/produtividade-user", {
        empresa_id: empresa?.id || user?.empresa_id,
        inicio: filtros.inicio,
        fim: filtros.fim
      });
      setDadosProdutividade(response.data?.data || response.data);
    } catch (err) {
      console.error("Erro ao carregar produtividade por utilizador:", err);
      notify("Erro ao carregar dados de produtividade.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (empresa?.id || user?.empresa_id) {
      fetchProdutividade();
    }
  }, [empresa?.id, user?.empresa_id]);

  const handleDateChange = (e) => {
    setFiltros({ ...filtros, [e.target.name]: e.target.value });
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    fetchProdutividade();
  };

  const listaProdutividade = dadosProdutividade?.produtividade_utilizadores || [];

  // Calcular totais gerais para os cartões resumo
  const totalGeralDocumentos = listaProdutividade.reduce((acc, curr) => acc + curr.total_documentos, 0);
  const totalGeralValor = listaProdutividade.reduce((acc, curr) => acc + curr.valor_total_gerado, 0);

  return (
    <ResourcePage title="Produtividade por Utilizador" breadcrumb={["Gestão Analítica", "Produtividade User"]}>
      {/* Filtro de Período */}
      <div className="card bg-white border-0 shadow-sm mb-4">
        <div className="card-body py-3">
          <form onSubmit={handleFilterSubmit} className="row align-items-center g-3">
            <div className="col-md-auto">
              <span className="fw-semibold text-muted d-flex align-items-center gap-1">
                <Calendar size={18} /> Período:
              </span>
            </div>
            <div className="col-md-3">
              <input 
                type="date" 
                className="form-control form-control-sm" 
                name="inicio" 
                value={filtros.inicio} 
                onChange={handleDateChange} 
                required
              />
            </div>
            <div className="col-md-3">
              <input 
                type="date" 
                className="form-control form-control-sm" 
                name="fim" 
                value={filtros.fim} 
                onChange={handleDateChange} 
                required
              />
            </div>
            <div className="col-md-auto">
              <button 
                type="submit" 
                className="btn btn-primary btn-sm px-4" 
                disabled={loading}
              >
                {loading ? "A atualizar..." : "Filtrar Produtividade"}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Cartões de Resumo */}
      <div className="row mb-4">
        <div className="col-xl-6 col-md-6 mb-4 mb-xl-0">
          <div className="card bg-white border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="text-primary bg-primary-subtle p-2 rounded d-inline-flex mb-3">
                <FileText size={22} />
              </div>
              <p className="text-muted mb-1 small">Total de Documentos Emitidos (Equipa)</p>
              <h4 className="mb-0 fw-bold">{totalGeralDocumentos} documentos</h4>
            </div>
          </div>
        </div>

        <div className="col-xl-6 col-md-6">
          <div className="card bg-white border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="text-success bg-success-subtle p-2 rounded d-inline-flex mb-3">
                <DollarSign size={22} />
              </div>
              <p className="text-muted mb-1 small">Valor Total Gerado (Equipa)</p>
              <h4 className="mb-0 text-success fw-bold">{Number(totalGeralValor).toLocaleString()} Kz</h4>
            </div>
          </div>
        </div>
      </div>

      {/* Tabela de Produtividade por Utilizador */}
      <div className="card bg-white border-0 shadow-sm">
        <div className="card-body">
          <h4 className="card-title mb-3 fs-5 d-flex align-items-center gap-2">
            <Activity size={20} className="text-primary" /> Desempenho da Equipa no Período
          </h4>
          <div className="table-responsive">
            <table className="table table-centered table-hover mb-0 align-middle">
              <thead>
                <tr className="text-muted fs-7">
                  <th>Utilizador</th>
                  <th className="text-center">Documentos Validados</th>
                  <th className="text-end">Valor Total Gerado</th>
                </tr>
              </thead>
              <tbody>
                {listaProdutividade.length > 0 ? (
                  listaProdutividade.map((item) => (
                    <tr key={item.user_id}>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <div className="bg-light text-secondary rounded-circle p-2 d-flex align-items-center justify-content-center" style={{ width: "36px", height: "36px" }}>
                            <Users size={18} />
                          </div>
                          <div>
                            <span className="fw-semibold d-block">{item.nome}</span>
                            <small className="text-muted">ID: {item.user_id}</small>
                          </div>
                        </div>
                      </td>
                      <td className="text-center fw-bold">
                        <span className="badge bg-primary-subtle text-primary px-2 py-1">
                          {item.total_documentos} docs
                        </span>
                      </td>
                      <td className="text-end fw-bold text-success">
                        {Number(item.valor_total_gerado).toLocaleString()} Kz
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="3" className="text-center text-muted py-4">Nenhum registo de produtividade encontrado para o período.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </ResourcePage>
  );
};

export default ProdutividadeUtilizadores;